import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createAddressApi,
  updateAddressApi,
  patchAddressApi,
  deleteAddressApi,
  deleteAddressesApi,
} from '../api/addressApi'
import { type Address } from '../../../utils/types'
import LogTool from '../../../logger/logTools'
import { contactKeys } from '../../Contact/utils/queryKeys'
import { addressKeys } from '../utils/queryKeys'

const log = new LogTool({ context: 'AddressMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new address
 */
export function useCreateAddress(): UseMutationResult<Address, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createAddressApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: addressKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      log.info('Address created successfully')
    },
    onError: (error) => {
      log.error('Error creating address:', error)
    },
  })
}

/**
 * Hook to update an address (full update with PUT)
 */
export function useUpdateAddress(): UseMutationResult<
  Address,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateAddressApi(idOrSelf, data),
    onSuccess: async (updatedAddress) => {
      await queryClient.invalidateQueries({ queryKey: addressKeys.lists() })
      await queryClient.invalidateQueries({
        queryKey: addressKeys.detail(updatedAddress.key)
      })
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      log.info('Address updated successfully')
    },
    onError: (error) => {
      log.error('Error updating address:', error)
    },
  })
}

/**
 * Hook to patch an address (partial update with PATCH)
 */
export function usePatchAddress(): UseMutationResult<
  Address,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => patchAddressApi(idOrSelf, data),
    onSuccess: async (updatedAddress) => {
      await queryClient.invalidateQueries({ queryKey: addressKeys.lists() })
      await queryClient.invalidateQueries({
        queryKey: addressKeys.detail(updatedAddress.key)
      })
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      log.info('Address patched successfully')
    },
    onError: (error) => {
      log.error('Error patching address:', error)
    },
  })
}

/**
 * Hook to delete a single address
 */
export function useDeleteAddress(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteAddressApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: addressKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      log.info('Address deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting address:', error)
    },
  })
}

/**
 * Hook to delete multiple addresses
 */
export function useDeleteAddresses(): UseMutationResult<void, Error, Address[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteAddressesApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: addressKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      log.info('Addresses deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting addresses:', error)
    },
  })
}

